import { FC, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUpdateEffect } from 'react-use';
import { datadogRum } from '@datadog/browser-rum';
import { twJoin, twMerge } from 'tailwind-merge';

import { useDagContext } from '@dbt-labs/dbt-dag';
import {
  Button,
  IconButton,
  RyeconClose,
  RyeconConfigure,
  RyeconLineage,
  RyeconList,
  RyeconRerun,
} from '@dbt-labs/sourdough';

import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useFilterContext } from '@/context/FilterProvider';
import { usePanel } from '@/context/PanelProvider';
import { useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParamsSafe } from '@/hooks/params';
import { safeParseUniqueId } from '@/typings/parseUniqueId';
import { isSubmitKey } from '@/util/input';
import { GranularityType, ViewType } from '@/util/linkTypes';
import { AccountSearchInput } from '../AccountSearchInput';
import { useCurrentPage } from '../Page';
import { SelectorInput } from '../SelectorInput';
import { SelectorTooltip } from '../SelectorTooltip';
import { SimpleLinkBreadcrumbs } from '../SimpleLinkBreadcrumbs';
import { SELECTOR_SYNTAX_DD_ACTION } from './constants';
interface LineageFloatingNavBarProps {
  projectName?: string;
  granularity: GranularityType;
  view: ViewType;
  accountName: string;
  projectId?: number;
  accountId: number;
}

export const LineageFloatingNavBar: FC<LineageFloatingNavBarProps> = ({
  granularity,
  view,
  projectName = '',
  projectId,
  accountName,
  accountId,
}) => {
  const [cursorPosition, setCursorPosition] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectParam = searchParams.get('select');
  const [rawSelector, setRawSelector] = useState<string | null>(selectParam ?? null);
  useUpdateEffect(() => {
    setRawSelector(selectParam);
  }, [selectParam]);
  const [couldShowAutocomplete, setCouldShowAutocomplete] = useState(false);
  const [autocompleteIndex, setAutocompleteIndex] = useState<number | undefined>(
    undefined,
  );
  const isCursorAtEnd = cursorPosition === rawSelector?.length;
  const isShowingAutocomplete =
    couldShowAutocomplete && (autocompleteIndex != null || isCursorAtEnd);

  const selectorInputContainerRef = useRef<HTMLDivElement | null>(null);

  const isProject = granularity === 'project';
  const isAccount = granularity === 'account';
  const isColumn = granularity === 'column';
  const isLineage = view === 'lineage';
  const multiProjectLink = useLinkToExplorer('multiProject');
  // TODO: re-enable panel
  const allowPanel = false;

  const panel = usePanel();
  const dagContext = useDagContext();
  const navigate = useNavigate();
  const { environmentId } = useDbtCloudProject();
  const overviewLink = useLinkToExplorer('project');
  const filterCtx = useFilterContext();
  const projectScope = useUrlProjectParamsSafe();

  /**
   * Clear the lineageIngressPath when the accountId, environmentId, or projectId changes
   */
  const { lineageIngressPath, setLineageIngressPath } = useCurrentPage();
  const setLineageIngressPathRef = useRef(setLineageIngressPath);
  setLineageIngressPathRef.current = setLineageIngressPath;
  useUpdateEffect(() => {
    setLineageIngressPathRef.current?.(null);
  }, [accountId, environmentId, projectId]);

  const uniqueIdFromParam = searchParams.get('uniqueId');
  const parsedUniqueId = uniqueIdFromParam
    ? safeParseUniqueId(uniqueIdFromParam).ok
    : undefined;
  const columnNameFromParam = searchParams.get('columnName');
  const getDetailsPageLink = useLinkToExplorer('resourceDetails');

  if (!accountId) return null;
  const breadcrumbs =
    !projectScope || isAccount
      ? null
      : isColumn
        ? [
            {
              text: accountName,
              href: multiProjectLink({
                accountId,
                tab: 'projects',
                view: 'lineage',
              }),
            },
            {
              text: projectName,
              href: overviewLink(projectScope),
            },
            {
              text: parsedUniqueId?.remainder || '',
              href: getDetailsPageLink({
                ...projectScope,
                tab: 'columns',
                dbtUniqueId: parsedUniqueId?.raw || '',
              }),
            },
            { text: columnNameFromParam || '' },
          ]
        : [
            {
              text: accountName,
              href: multiProjectLink({
                accountId,
                tab: 'projects',
                view: 'lineage',
              }),
            },
            { text: projectName },
          ];
  const defaultReturnPath = isAccount
    ? multiProjectLink({
        accountId,
        tab: 'projects',
      })
    : !projectScope
      ? null
      : isColumn
        ? getDetailsPageLink({
            ...projectScope,
            tab: 'columns',
            dbtUniqueId: parsedUniqueId?.raw || '',
          })
        : overviewLink(projectScope);

  // The z index here is 30 because the dropdowns in Cloud-UI are z-40
  return (
    <div className={twMerge('w-full', view === 'lineage' && 'absolute z-30')}>
      <div className="mx-6 mt-5 flex items-center space-x-2 whitespace-nowrap text-fgMain">
        {defaultReturnPath && (
          <Button
            type="secondary"
            ryecon={RyeconClose}
            className="flex-0 justify-center"
            testId="close-lineage-x-button"
            tooltip="Exit Full Screen"
            onClick={() => {
              navigate(lineageIngressPath || defaultReturnPath);
            }}
          />
        )}
        {breadcrumbs && (
          <span className="flex-0 hidden sm:flex">
            <SimpleLinkBreadcrumbs
              className="h-10 rounded-md border border-borderMain bg-bgMain px-4 py-3 text-xs"
              breadcrumbs={breadcrumbs}
            />
          </span>
        )}
        {isProject && (
          <div className="relative mr-auto flex-1" ref={selectorInputContainerRef}>
            <div className="mr-auto flex-1">
              <SelectorInput
                name="Selector input"
                className="ml-auto w-full max-w-[1000px]"
                autoFocus
                placeholder={isAccount ? '' : undefined}
                value={rawSelector ?? ''}
                endIconEnabled={!isAccount}
                trackingId="explore-selector-input"
                onSelect={(e) => {
                  if (
                    'selectionStart' in e.target &&
                    typeof e.target.selectionStart === 'number'
                  ) {
                    setCursorPosition(e.target.selectionStart ?? 0);
                  }
                }}
                onChange={(e) => {
                  if (e.target.selectionStart != null) {
                    setCursorPosition(e.target.selectionStart ?? 0);
                  }
                  setRawSelector(e.target.value);
                  setAutocompleteIndex(undefined);
                }}
                onFocus={(e) => {
                  setCouldShowAutocomplete(true);
                  setAutocompleteIndex(undefined);
                  setRawSelector(e.target.value ?? '');
                }}
                onBlur={(e) => {
                  // Stop propagation if e.relatedTarget is a child or selectorInputContainerRef.current
                  if (
                    e.relatedTarget &&
                    selectorInputContainerRef.current?.contains(e.relatedTarget)
                  ) {
                    e.stopPropagation();
                    e.target.focus();
                    return;
                  }
                  setCouldShowAutocomplete(false);
                  setAutocompleteIndex(undefined);
                }}
                onKeyDown={(e) => {
                  /**
                   * Submits the current input as a selector
                   */
                  function submitSelector(e: React.KeyboardEvent<HTMLInputElement>) {
                    if ('blur' in e.target && typeof e.target.blur === 'function') {
                      e.target.blur();
                    }

                    // Adds a custom action so we can track the selector syntax usage
                    datadogRum.addAction(SELECTOR_SYNTAX_DD_ACTION, {
                      selector: rawSelector ?? '',
                    });

                    setSearchParams((params) => {
                      params.set('select', rawSelector ?? '');
                      return params;
                    });
                  }

                  /**
                   * Switches between visible autocomplete options
                   */
                  function navigateOptions(e: React.KeyboardEvent<HTMLInputElement>) {
                    e.preventDefault();
                    setAutocompleteIndex((prev) => {
                      if (prev === undefined) {
                        return e.key === 'ArrowDown' ? 0 : -1;
                      }
                      return prev + (e.key === 'ArrowDown' ? 1 : -1);
                    });
                  }

                  /**
                   * Resolves current input, and requests the next suggestion.
                   *
                   * Typing literally any character will request more completions,
                   * but some users may want to see more options before typing
                   * a next character.
                   */
                  function requestMoreCompletions(
                    e: React.KeyboardEvent<HTMLInputElement>,
                  ) {
                    e.preventDefault();
                    setAutocompleteIndex(undefined);
                  }

                  if (!isShowingAutocomplete) {
                    if (isSubmitKey(e)) {
                      submitSelector(e);
                    }
                    return;
                  }

                  function shouldRequestMoreCompletions() {
                    if (autocompleteIndex == null) return false;
                    return [':', 'config.'].some((unresolvedEnding) =>
                      rawSelector?.trimEnd()?.endsWith(unresolvedEnding),
                    );
                  }

                  switch (e.key) {
                    case 'ArrowDown':
                    case 'ArrowUp':
                      navigateOptions(e);
                      break;
                    case 'ArrowRight':
                    case 'Tab':
                      requestMoreCompletions(e);
                      break;
                    case ' ':
                      if (shouldRequestMoreCompletions()) {
                        requestMoreCompletions(e);
                      }
                      break;
                    case 'Enter':
                    case 'NumpadEnter':
                      if (shouldRequestMoreCompletions()) {
                        requestMoreCompletions(e);
                      } else {
                        submitSelector(e);
                      }
                      break;
                  }
                }}
              />
            </div>
            <SelectorTooltip
              show={isShowingAutocomplete}
              className="absolute right-0 ml-auto mt-1 w-full max-w-[1000px]"
              textInput={rawSelector}
              updateSelector={setRawSelector}
              selectedIndex={autocompleteIndex}
              setSelectedAutocomplete={setRawSelector}
            />
          </div>
        )}
        {isAccount && (
          <AccountSearchInput className="mr-auto max-w-[700px] flex-1" autoFocus />
        )}
        {isAccount && (
          <>
            <IconButton
              ryecon={RyeconLineage}
              className={twJoin('p-3', view === 'lineage' && 'bg-bgMain')}
              tooltip={'Lineage view'}
              trackingId="explorer-account-page-account-lineage"
              onClick={() => {
                searchParams.set('view', 'lineage');
                setSearchParams(searchParams);
              }}
            />
            <IconButton
              ryecon={RyeconList}
              className={twJoin('p-3', view !== 'lineage' && 'bg-bgMain')}
              tooltip={'List view'}
              trackingId="explorer-account-page-account-list-view"
              onClick={() => {
                searchParams.delete('view');
                setSearchParams(searchParams);
              }}
            />
          </>
        )}
        {!isAccount && allowPanel && (
          <Button
            type="secondary"
            ryecon={RyeconConfigure}
            iconPlacement="right"
            tooltip="Toggle filters"
            testId="toggle-filter-panel-button"
            onClick={() => {
              panel.toggleFilterPanel();
            }}
          >
            {filterCtx.activeFilterCount > 0 && (
              <span className="rounded bg-bgBlack px-1 leading-tight text-fgWhite">
                {filterCtx.activeFilterCount}
              </span>
            )}
          </Button>
        )}
        <Button
          className={twJoin(!isLineage && 'invisible')}
          type="secondary"
          isDisabled={!isLineage}
          ryecon={RyeconRerun}
          tooltip={isLineage ? 'Reset' : undefined}
          onClick={() => {
            dagContext.resetDagLayoutAndZoom();
          }}
        />
      </div>
    </div>
  );
};
